package com.swetdot.swetgram.ui.groups

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.groups.toDomain
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.Group
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.dto.SearchResponse
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.text
import io.ktor.client.request.parameter
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.launch
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.groups_list_error_load
import swetgram.shared.generated.resources.groups_list_error_load_more
import swetgram.shared.generated.resources.groups_list_error_search

/**
 * The Groups TAB — SPS §7.1 and SDS §27.8. The list of groups the caller belongs to.
 *
 * A NOTE ON THE SECTION NUMBERS. The task named "SPS §11 Groups"; in the copy of `swetgram_sps.md`
 * in the reference folder §11 is Discover and GROUPS IS §12. Every citation in this package points
 * at the text as it actually reads, so §12.1-§12.7 is the groups chapter.
 *
 * WHAT CHANGED IN THIS PASS, AND WHY THE FILE GREW
 *
 * The user's instruction was "on groups to be like a conversation list chats like chats": the tab
 * becomes the CHAT LIST, for groups. That is mostly `GroupsScreen`'s problem and it needs two
 * things from here that the old state did not carry.
 *
 *  1. **THE CONVERSATION UNDER EACH GROUP.** The contract's `Group` carries a name, a description,
 *     a member count and a role, and NOTHING a chat-list row is made of: no last message, no
 *     timestamp, no unread count, no mute flag, no member faces. Those live on the CONVERSATION,
 *     which the client already has — `ChatRepository.observeChats` reads the SQLDelight cache the
 *     Chats tab paints from, and `Group.conversationId` is exactly the key into it. So [conversations]
 *     is joined in here rather than fetched: no new endpoint, no new repository method, no second
 *     read of anything. A group whose conversation is not cached yet still draws, on
 *     `groupRowSubtitle` — see `GroupsScreen.groupPreviewLine`.
 *  2. **SEARCH THAT ACTUALLY SEARCHES.** See [GroupSearchSource].
 *
 * WHY THE FIRST FRAME IS STILL A SKELETON
 *
 * There is no `groups` table, so there is nothing to paint the LIST from and the honest first frame
 * is a row of placeholders (SDS §25 lists Loading among the states every component owes, and §2.1
 * prefers a placeholder that holds the layout to a spinner that reflows it). The conversation join
 * above does not change that: it can decorate a group that is already on screen, and it knows
 * nothing about which groups exist.
 *
 * Follows `ChatListViewModel` and its neighbours in this package: an androidx `ViewModel`, work on
 * `viewModelScope`, state as a `StateFlow`, `CancellationException` rethrown before any other
 * `Throwable`, and a failure reported ABOVE the list rather than in place of it.
 */
class GroupsViewModel(
    private val groups: GroupRepository,
    /**
     * The cached conversations, for the chat-list row. `ChatRepository.observeChats(archived = false)`.
     *
     * A `Flow` rather than the repository, so this view model does not acquire a second dependency
     * it would only ever call one method on — and so a test can hand it a list. Empty by default:
     * a caller that does not supply it gets rows built from the `Group` alone, which is what this
     * screen drew before the re-skin. It is NOT optional in the app; see the note in
     * `ui/Navigation.kt` about where it is passed.
     */
    conversations: Flow<List<Chat>> = emptyFlow(),
    /** Null draws NO search field rather than an inert one. See [GroupSearchSource]. */
    private val search: GroupSearchSource? = null,
    /**
     * The signed-in user, needed only to decide whether a row's preview reads "Me:" or a member's
     * name — `Chat.preview` cannot answer that without knowing who "I" am.
     *
     * Null in previews and tests, which have no session. A null one falls the row back to the
     * group's own subtitle rather than guessing a speaker, which is the honest degradation: naming
     * the wrong person on the home row of a tab is worse than naming nobody.
     */
    me: UserId? = null,
) : ViewModel() {

    private val _state = MutableStateFlow(GroupsUiState(me = me, canSearch = search != null))
    val state: StateFlow<GroupsUiState> = _state.asStateFlow()

    /** Opaque and server-owned. Never parsed, compared, or kept across a sign-out. */
    private var cursor: String? = null

    /** A page request is in flight. Guards the paging footer against itself. */
    private var paging = false

    private var loading = false

    private var searchJob: Job? = null
    private var searchGeneration: Long = 0

    init {
        // QUIETLY, because the first read already has a way to say it is happening: with nothing
        // held the screen draws skeletons off `isFirstLoad`. A spinner on top of them is a second
        // statement about the same read.
        refreshQuietly()
        // COLLECTED, not combined into `state` downstream. `ui/Navigation.kt` reads
        // `viewModel.state.value.loadedOnce` from a `LaunchedEffect` to decide whether returning to
        // this tab should re-read; a `stateIn(WhileSubscribed)` over a combine would answer that
        // question from the INITIAL value before anything collects, and the tab would silently stop
        // refreshing on return. Writing into `_state` keeps `.value` the real current state.
        viewModelScope.launch {
            conversations.collect { chats ->
                _state.value = _state.value.copy(
                    conversations = chats.associateBy { it.id.value },
                )
            }
        }
    }

    /**
     * Re-reads the first page.
     *
     * The cursor is DROPPED here, not kept: one from a previous page set points into a list that no
     * longer exists, and continuing from it after a group was created or left would repeat a row or
     * skip one. `GroupMembersViewModel` and `BlockedUsersViewModel` both do this, for the same
     * reason.
     *
     * This is also the path back from the create flow — the caller refreshes on resume rather than
     * the new group being spliced in locally, so the list the user lands on is the one the server
     * actually holds.
     *
     * THIS ONE IS THE USER'S: the pull gesture and the banner's Retry. The indicator is the receipt
     * for a gesture, which is why this and [refreshQuietly] are two entry points and not one.
     */
    fun refresh() {
        read(announce = true)
    }

    /**
     * The same read, with nothing on screen to show for it.
     *
     * WHAT THE USER ACTUALLY REPORTED. Re-entering the Groups tab re-runs the list read — it has
     * to, because there is no cache and no realtime frame for groups, so coming back from the
     * create flow or from a group that was just left is the only way this list ever changes (see
     * `ui/Navigation.kt`). That read is CORRECT and stays. What was wrong is that it went through
     * [refresh] and so raised the pull-to-refresh spinner, over a list whose every row was already
     * on screen and which came back identical.
     *
     * `refreshing` is the ONLY thing that changes here. `groups` is never cleared by a read — see
     * the catch below, which has always left the list alone — so the rows the user is looking at
     * stay put while this runs.
     */
    fun refreshQuietly() {
        read(announce = false)
    }

    private fun read(announce: Boolean) {
        // Set BEFORE the in-flight test, so a pull during a background read still gets its
        // indicator: the read the gesture asked for is genuinely running, and the `finally` below
        // takes the indicator down when it settles.
        if (announce) _state.value = _state.value.copy(refreshing = true)
        // A second Retry while the first read is in flight would produce two banners for one
        // failure, and two pages for one list.
        if (loading) return
        loading = true
        _state.value = _state.value.copy(refreshing = announce, error = null)
        viewModelScope.launch {
            try {
                val page = groups.groups()
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    // DECISIONS §7 fixes `listGroups` as "ordered by creation, newest first", and
                    // explains why it is creation rather than activity: a keyset cursor over a
                    // column that moves under the reader repeats a row or skips one. The client
                    // does not re-sort — doing so would put the page boundaries in the wrong place.
                    groups = page.items,
                    hasMore = page.hasMore,
                    loadedOnce = true,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // The LIST IS LEFT ALONE. Whatever is on screen is still the last thing the server
                // actually said, and it is more useful than an apology.
                //
                // `groupBanner(UiText)` rather than `groupBanner(String)`: the sentence is a key
                // now, which is the whole point of this file's half of the localisation pass. That
                // overload's own TODO asked for exactly this caller.
                _state.value = _state.value.copy(
                    loadedOnce = true,
                    error = error.groupBanner(Res.string.groups_list_error_load.text()),
                )
            } finally {
                loading = false
                _state.value = _state.value.copy(refreshing = false)
            }
        }
    }

    /** The next page, driven by the footer scrolling into view. */
    fun loadMore() {
        val current = _state.value
        if (paging || loading || !current.hasMore) return
        val next = cursor ?: return
        paging = true
        _state.value = current.copy(loadingMore = true)
        viewModelScope.launch {
            try {
                val page = groups.groups(cursor = next)
                cursor = page.nextCursor
                _state.value = _state.value.copy(
                    groups = _state.value.groups + page.items,
                    hasMore = page.hasMore,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                // A failed PAGE stops the paging rather than emptying the list: `hasMore` goes
                // false so the footer does not sit there asking again forever, and the banner says
                // what happened. Pull-to-refresh is the way back, which is why the gesture exists.
                _state.value = _state.value.copy(
                    hasMore = false,
                    error = error.groupBanner(Res.string.groups_list_error_load_more.text()),
                )
            } finally {
                paging = false
                _state.value = _state.value.copy(loadingMore = false)
            }
        }
    }

    // ------------------------------------------------------------------ search (SPS §14.2)

    /**
     * Types into the search field.
     *
     * THE LOOKUP IS REMOTE AND ONLY REMOTE. Filtering the page already in memory would answer
     * instantly and would be a different feature wearing this one's clothes: the list holds the
     * groups the caller BELONGS TO, and `globalSearch` answers about the groups that EXIST. A user
     * typing a name they have not joined would get "No results found." from a client that never
     * asked. So there is no local pre-filter here, deliberately, and the price is a request and a
     * debounce.
     *
     * Debounced for the reason `ChatListViewModel.onQueryChange` gives: SDS §11.3 wants results as
     * the user types and DECISIONS §12's 60-requests-per-minute budget does not want one per
     * character.
     */
    fun onQueryChange(next: String) {
        val generation = ++searchGeneration
        _state.value = _state.value.copy(query = next)
        searchJob?.cancel()
        // The previous verdict was about a query that is no longer on screen. Keeping it would put
        // "No connection" over results that have since arrived.
        _state.value = _state.value.copy(searchError = null)

        val trimmed = next.trim()
        if (trimmed.isEmpty()) {
            _state.value = _state.value.copy(
                results = emptyList(),
                searching = false,
                searchSettled = false,
            )
            return
        }

        val client = search ?: return

        _state.value = _state.value.copy(searching = true)
        searchJob = viewModelScope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            try {
                val found = client.searchGroups(trimmed.removePrefix("@"))
                if (generation != searchGeneration) return@launch
                _state.value = _state.value.copy(results = found)
                settle()
            } catch (cancellation: CancellationException) {
                // NOT settled, and deliberately not in a `finally`. This job is cancelled by the
                // NEXT keystroke, which has already set `searching = true` for its own request — so
                // a `finally` here would run afterwards and report the newer query as answered with
                // the older query's empty list, and the screen would say "No results found." about
                // a search still in flight. The chat list learned this the hard way.
                throw cancellation
            } catch (error: Throwable) {
                if (generation != searchGeneration) return@launch
                // NAMED, not swallowed, and this is the defect the chat list fixed two days ago:
                // an empty result and a failed lookup are byte-identical on screen otherwise, and
                // the screen falls through to SDS §17.2's "No results found." — a statement about
                // the server's records that a client with no answer cannot make.
                _state.value = _state.value.copy(
                    results = emptyList(),
                    searchError = error.groupBanner(Res.string.groups_list_error_search.text()),
                )
                settle()
            }
        }
    }

    /** This query has produced an answer — of some kind. Both non-cancellation paths end here. */
    private fun settle() {
        _state.value = _state.value.copy(searching = false, searchSettled = true)
    }

    /** Runs the current query again. The banner's Retry, for a failure the user can act on. */
    fun retrySearch() {
        onQueryChange(_state.value.query)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }

    private companion object {
        /**
         * Long enough that a word typed at speed is one request, short enough that the results feel
         * like they are keeping up. The same number the chat list uses, because it is the same box.
         */
        const val SEARCH_DEBOUNCE_MS = 250L
    }
}

// ---------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------

/**
 * Where a group search comes from.
 *
 * THIS IS THE ONE OPERATION IN THE FROZEN CONTRACT THAT ANSWERS THIS QUESTION. `globalSearch`
 * returns `people`, `groups` and `conversations`, and the `groups` array has been arriving with
 * nowhere to go since the tab was built — this file's own KDoc used to say so and used to conclude
 * that discovery should be left out, because `POST /join/{invite_token}` needs a token and a search
 * result would lead nowhere. THAT CONCLUSION IS NARROWED RATHER THAN REVERSED: a result the caller
 * is ALREADY IN opens, and one they are not in opens `getPublicGroupByUsername`'s read-only screen,
 * which is a real destination even though it cannot admit anybody. What is still true is that this
 * build cannot JOIN from a search result, and nothing here pretends otherwise.
 *
 * An interface rather than the call, so the pure half of this screen can be tested without an
 * `ApiClient` — the constraint every view model in this package records.
 */
fun interface GroupSearchSource {
    suspend fun searchGroups(query: String): List<Group>
}

/**
 * [GroupSearchSource] over the contract's `globalSearch`.
 *
 * TODO(integration): this belongs in `data/` beside `GroupRepository`, or in the `SearchRepository`
 * that `ui/chats/ChatCommands` also says it is waiting for — there are now TWO callers of
 * `GET /search` in the UI layer reading two different arrays out of one response, and one request
 * would answer both. It is here because this pass owned this file and not `data/`. Moving it is a
 * file move plus a constructor argument; nothing outside this file reaches past it.
 *
 * Only the `groups` array is read. `people` is the chat list's business and `conversations` is
 * redundant against a cache that already holds every conversation the caller has.
 */
class GroupSearchCommands(private val api: ApiClient) : GroupSearchSource {

    override suspend fun searchGroups(query: String): List<Group> {
        val response: SearchResponse = api.get("search") {
            parameter("q", query)
            parameter("limit", SEARCH_LIMIT)
        }
        // The boundary's own mapper, `data/groups/GroupRepository.kt`'s `GroupDto.toDomain`. A
        // second one here would be a second place for the visibility and role fail-closed rules to
        // drift, which is the whole reason that function is `internal` rather than private.
        return response.groups.map { it.toDomain() }
    }

    private companion object {
        /** Enough to fill a phone screen; more would just be scrolled past. */
        const val SEARCH_LIMIT = 20
    }
}

// ---------------------------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------------------------

/**
 * SDS §25's states for this screen, as one value.
 *
 * [loadedOnce] is what keeps an empty list from being called "no groups" before anybody has looked.
 * Without it a user with twelve groups sees the empty state for as long as the request takes, and
 * is invited to create a group they already have.
 */
data class GroupsUiState(
    val groups: List<Group> = emptyList(),
    val loadedOnce: Boolean = false,
    /**
     * A first-page read THE USER ASKED FOR is in flight; drives the pull-to-refresh indicator.
     *
     * Not "a read is running" — `GroupsViewModel.loading` is that. It was both, and a screen that
     * re-reads on every return therefore raised a spinner over a full list every time the tab was
     * opened. See `GroupsViewModel.refreshQuietly`.
     */
    val refreshing: Boolean = false,
    val loadingMore: Boolean = false,
    val hasMore: Boolean = false,
    /**
     * Why the LIST read failed, as a message rather than a sentence.
     *
     * `UiText` rather than `String` as of the localisation pass: a view model cannot call
     * `stringResource`, so it names the message and the screen resolves it where it draws.
     */
    val error: UiText? = null,
    // ------------------------------------------------------------------ the chat-list row
    /**
     * The cached conversation for each group, by CONVERSATION id — `Group.conversationId.value`.
     *
     * Everything the chat-list row shows beyond the name comes from here: the last message, its
     * time, the unread count, the mute bell and the member faces. A group missing from this map is
     * not an error; it is a group whose conversation the cache has not seen yet, and the row falls
     * back to the group's own subtitle.
     */
    val conversations: Map<String, Chat> = emptyMap(),
    /** Who "I" am, for the preview's speaker. Null in previews; see `GroupsViewModel.me`. */
    val me: UserId? = null,
    // ------------------------------------------------------------------------- search
    val query: String = "",
    /** Remote results from `globalSearch`. Empty until a query settles. */
    val results: List<Group> = emptyList(),
    val searching: Boolean = false,
    /** False until a query has produced an answer, so "no results" is not shown before one. */
    val searchSettled: Boolean = false,
    /**
     * Why the last group lookup failed, or null when it produced an answer.
     *
     * Its own channel rather than [error], which is about the LIST. A search that could not run
     * must not make the tab look broken, and — far more important — it must not be allowed to fall
     * through to "No results found.", which is a statement about the server's records that a client
     * with no answer cannot make.
     */
    val searchError: UiText? = null,
    /**
     * There is a search source behind the field.
     *
     * False draws NO field rather than an inert one, the same rule every nullable action on this
     * screen follows. A box that swallows what is typed and answers nothing is worse than no box:
     * whatever it rendered — skeletons forever, or "No results found." — would be a claim nobody
     * asked the server about.
     */
    val canSearch: Boolean = false,
) {
    /** Nothing read yet and nothing cached: the frame that gets skeletons rather than a verdict. */
    val isFirstLoad: Boolean get() = groups.isEmpty() && !loadedOnce

    /**
     * Read, and empty.
     *
     * [loadedOnce] is set by the FAILURE branch as well as the success branch — see `refresh` — so
     * that a failed first read settles the screen instead of spinning skeletons that never resolve.
     * This means "somebody looked", not "the server said there is nothing".
     */
    val isEmpty: Boolean get() = groups.isEmpty() && loadedOnce

    /**
     * The one state that may say "No groups yet." OUT LOUD, with its illustration and its
     * "New group" call to action.
     *
     * On a failed first read the screen drew that and the failure banner at the same time: two
     * contradictory statements, of which the larger and centred one was the false one. `loadedOnce`
     * exists to stop the claim being made before anybody looked, and a read that failed did not
     * look. Kept as a separate property from [isEmpty] so the paging guards, which genuinely want
     * "has a read settled", are not quietly given a different question to answer.
     */
    val showsEmptyState: Boolean get() = isEmpty && error == null

    /** A query is on screen, so the list underneath is a search's answer rather than the tab. */
    val isSearching: Boolean get() = query.isNotBlank()
}
